import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useStoreActions } from '../../models/RootStore';
import useCoolPoll from '../useCoolPoll';

/**
 * Hook for managing Modbus schedules CRUD operations
 * Handles fetching, creating, updating, and deleting schedules.
 * Pass `skipFetch` for a consumer that only needs the write actions (e.g. the
 * add/edit dialog) and never reads the schedules list, so mounting it doesn't
 * trigger a schedules fetch that'll just go unused.
 */
const useModbusSchedules = (modbusUnitId, { skipFetch = false } = {}) => {
  // State management
  const [schedules, setSchedules] = useState(null);
  const [isLoading, setIsLoading] = useState(!skipFetch);
  const [error, setError] = useState(null);

  // Store actions
  const {
    getModbusSchedules,
    updateSchedule: updateScheduleAction,
    deleteSchedule: deleteScheduleAction,
    createModbusSchedule,
    getSchedule,
  } = useStoreActions((actions) => actions.schedules);

  const addMessage = useStoreActions((actions) => actions.errorMessage.addMessage);

  // Fetch schedules for the Modbus unit
  const fetchSchedules = useCallback(async ({ silent = false } = {}) => {
    if (!modbusUnitId) {
      setError('No Modbus unit ID provided');
      return;
    }

    try {
      if (!silent) {
        setIsLoading(true);
      }
      setError(null);

      const response = await getModbusSchedules({
        modbusId: modbusUnitId,
        type: '0',
      });

      // Extract schedules data from the response structure
      const fetchedSchedules = response?.data || response;
      setSchedules(fetchedSchedules);
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch schedules';
      setError(errorMessage);
      addMessage({ message: errorMessage });
    } finally {
      if (!silent){
        setIsLoading(false);
      }
    }
  }, [modbusUnitId, getModbusSchedules, addMessage]);

  // Keep ref to schedules for stable polling callback
  const schedulesRef = useRef(schedules);
  useEffect(() => {
    schedulesRef.current = schedules;
  }, [schedules]);

  // Create a new schedule
  const createSchedule = useCallback(async (scheduleData) => {
    if (!modbusUnitId) {
      throw new Error('No Modbus unit ID provided');
    }

    try {
      setError(null);

      const response = await createModbusSchedule({
        modbusId: modbusUnitId,
        data: scheduleData,
      });

      // Extract created schedule from response structure
      const createdSchedule = response?.data || response;

      // Update local schedules state
      setSchedules((prevSchedules) => ({
        ...prevSchedules,
        [createdSchedule.id]: createdSchedule,
      }));

      return createdSchedule;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create schedule';
      setError(errorMessage);
      addMessage({ message: errorMessage });
      throw err;
    }
  }, [modbusUnitId, createModbusSchedule, addMessage]);

  // Update an existing schedule
  const updateSchedule = useCallback(async (scheduleId, updateData) => {
    try {
      setError(null);

      const response = await updateScheduleAction({
        id: scheduleId,
        data: updateData,
      });

      // Extract updated schedule from response structure
      const updatedSchedule = response?.data || response;

      // Update local schedules state
      setSchedules((prevSchedules) => ({
        ...prevSchedules,
        [scheduleId]: {
          ...prevSchedules?.[scheduleId],
          ...updatedSchedule,
        },
      }));

      return updatedSchedule;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update schedule';
      setError(errorMessage);
      addMessage({ message: errorMessage });
      throw err;
    }
  }, [updateScheduleAction, addMessage]);

  // Delete a schedule
  const deleteSchedule = useCallback(async (scheduleId) => {
    try {
      setError(null);

      await deleteScheduleAction(scheduleId);

      // Remove from local schedules state
      setSchedules((prevSchedules) => {
        if (!prevSchedules) {
          return prevSchedules;
        }
        // eslint-disable-next-line no-unused-vars
        const { [scheduleId]: deleted, ...remaining } = prevSchedules;
        return remaining;
      });

      return true;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete schedule';
      setError(errorMessage);
      addMessage({ message: errorMessage });
      throw err;
    }
  }, [deleteScheduleAction, addMessage]);

  // Toggle schedule active/inactive state
  const toggleScheduleState = useCallback(async (schedule) => {
    const { id, isDisabled } = schedule;
    return updateSchedule(id, { isDisabled: !isDisabled });
  }, [updateSchedule]);

  // Merge an already-saved schedule (e.g. from a create/update made through a
  // different mounted instance of this hook, like the edit dialog) into local
  // state, so the list reflects it without a redundant full refetch. Merged
  // onto any existing entry rather than replacing it outright, in case the
  // save response only carries the changed fields.
  const mergeSchedule = useCallback((schedule) => {
    if (!schedule?.id) {
      return;
    }
    setSchedules((prevSchedules) => ({
      ...prevSchedules,
      [schedule.id]: {
        ...prevSchedules?.[schedule.id],
        ...schedule,
      },
    }));
  }, []);

  // Get schedules as array
  const schedulesArray = useMemo(() => {
    if (!schedules) {
      return [];
    }
    return Object.values(schedules);
  }, [schedules]);

  // Initial fetch on mount or when modbusUnitId changes
  useEffect(() => {
    if (modbusUnitId && !skipFetch) {
      fetchSchedules();
    }
  }, [modbusUnitId, skipFetch, fetchSchedules]);

  // Check if any schedule is in syncing state
  const isAnyScheduleSyncing = useMemo(() => {
    return schedulesArray.some(schedule => schedule.isSynced === 1);
  }, [schedulesArray]);

  const pollSyncingSchedules = useCallback(async () => {
    const currentSchedules = schedulesRef.current;
    if (!currentSchedules) {
      return;
    }
    const syncingSchedules = Object.values(currentSchedules)
      .filter(s => s.isSynced === 1);

    if (syncingSchedules.length === 0) {
      return;
    }

    await Promise.all(syncingSchedules.map(async (schedule) => {
      try {
        const refreshedSchedule = await getSchedule(schedule.id);

        setSchedules((prevSchedules) => ({
          ...prevSchedules,
          [schedule.id]: {
            ...prevSchedules?.[schedule.id],
            ...refreshedSchedule,
          },
        }));
      } catch (err) {
        console.error(`Failed to poll schedule ${schedule.id}`, err);
      }
    }));
  }, [getSchedule]);

  // Polling for syncing schedules
  useCoolPoll({
    enabled: isAnyScheduleSyncing,
    onPoll: pollSyncingSchedules,
    frequency: 2000,
  });

  return {
    // Data
    schedulesArray,
    // State
    isLoading,
    error,
    // Actions
    createSchedule,
    updateSchedule,
    deleteSchedule,
    toggleScheduleState,
    mergeSchedule,
    // Utilities
    refetch: fetchSchedules,
  };
};

export default useModbusSchedules;
